<template>
  <v-card outlined class="outlined mb-6 elevation-0 rounded-lg">
    <v-toolbar color="transparent" flat class="text-h5 mb-2 rounded-0 py-1">
      <span v-if="!searchOn" data-testid="toolbar-title">Follow-Up Calls Due Today</span>
      <v-layout v-else class="mt-4">
        <v-autocomplete data-testid="follow-up-location-filter-menu" class="follow-up-location-filter-menu pt-2 pb-2"
          align-with-title :items="facilityCodes" v-model="filteredLocationCode" label="Filter by location code..."
          :hint="
            !filteredLocationCode
              ? 'Enter or select a location code'
              : 'Click X to reset the list'
          " persistent-hint clearable></v-autocomplete>
        <v-switch data-testid="follow-up-filtered-today-switch" class="follow-up-filtered-today-switch mt-3 ml-5"
          label="Scheduled for Today" v-model="filteredTodayOnly" />
      </v-layout>
      <v-spacer />
      <v-btn data-testid="follow-up-search-btn" class="follow-up-search-btn" icon @click="toggleSearch">
        <v-icon>filter_list</v-icon>
      </v-btn>
    </v-toolbar>
    <v-data-table :loading="loading" :headers="headers" :items="selectedFollowups" no-data-text="No follow-ups found"
      loading-text="Loading follow-ups...">
      <template v-slot:[`item.customer_first_name`]="{ item }">
        <td data-testid="follow-up-name">
          {{ item.customer_first_name }} {{ item.customer_last_name }}
        </td>
      </template>
      <template v-slot:[`item.customer_phone`]="{ item }">
        <td data-testid="follow-up-phone">{{ addDashes(item.customer_phone) }}</td>
      </template>
      <template v-slot:[`item.location_code`]="{ item }">
        <td data-testid="follow-up-location-code">{{ item.location_code }}</td>
      </template>
      <template v-slot:[`item.move_in_date`]="{ item }">
        <td data-testid="follow-up-move-in">{{ formatDateUTC(item.move_in_date) }}</td>
      </template>
      <template v-slot:[`item.followup_at`]="{ item }">
        <td data-testid="follow-up-follow-up">
          {{ formatDate(item.followup_at) }}
        </td>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn data-testid="call-follow-up-btn" class="call-followup-btn primary--text" small
          @click.stop="openCallModal(item)" :disabled="phoneActive">
          <v-icon small>phone</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <application-modal v-if="selectedContact" v-model="initCallModal" @collapse="clearContact"
      data-testid="init-call-modal" width="66%">
      <div slot="modal-title">Initiate Follow-up Call</div>
      <div slot="modal-body" style="height: 37rem">
        <v-tabs show-arrows v-model="tab">
          <v-tab data-testid="follow-up-contact-details-tab" class="follow-up-contact-details-tab">
            Contact Details
          </v-tab>
          <v-tab data-testid="follow-up-contact-notes-tab" class="follow-up-contact-notes-tab">
            Contact Notes
          </v-tab>
          <v-tab data-testid="follow-up-add-note-tab" class="follow-up-add-note-tab">
            Add Note
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item eager>
            <v-layout v-if="unitFeaturesLoading || promotionOptionsLoading" justify-center>
              <v-progress-circular :size="60" :width="6" indeterminate class="my-3" />
            </v-layout>
            <follow-up-details v-else v-model="callId" :facilityInfo="facilityInfo" :promotionOffers="promotionOffers"
              :selectedContact="selectedContact" :unitFeatures="unitFeatures" />
          </v-tab-item>
          <v-tab-item eager>
            <v-layout v-if="noteLoading" justify-center>
              <v-progress-circular :size="60" :width="6" indeterminate class="my-3" />
            </v-layout>
            <v-layout v-else-if="!noteLoading && notes.length" column
              style="height: 31rem; outline: 1px grey solid; overflow-y: auto" class="mt-4 mb-3">
              <span class="ml-3 mt-3 mb-2 text-h6">
                Notes for {{ selectedContact.first_name }}
                {{ selectedContact.last_name }}:
              </span>
              <v-divider class="mx-3 mt-1" />
              <v-layout column>
                <v-flex v-for="(note, i) of notes" :key="i" class="mx-3 my-2">
                  <v-layout column class="my-1">
                    {{ note.description }}
                    <v-layout class="mt-1" align-center>
                      <v-icon class="mr-1">create</v-icon>
                      <span class="text-caption">Created on {{ timestamp(note.created_at) }}</span>
                    </v-layout>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-layout>
            <v-layout v-else class="pa-2 mt-3 text-h6">
              There are no notes for {{ selectedContact.first_name }}
              {{ selectedContact.last_name }}.
            </v-layout>
          </v-tab-item>
          <v-tab-item eager>
            <v-textarea data-testid="follow-up-add-note-textarea" class="follow-up-add-note-textarea px-2 pt-4"
              v-model="note" filled
              hint="When you are finished, click 'Add Note' to save this note to this tenant's account."
              label="Add Note" />
            <v-layout justify-end>
              <v-btn data-testid="follow-up-add-note-btn" class="follow-up-add-note-btn success" @click="addNote"
                :loading="noteLoading" :disabled="!note" small>
                Add Note
              </v-btn>
            </v-layout>
          </v-tab-item>
        </v-tabs-items>
      </div>
      <v-btn data-testid="follow-up-begin-call-btn" class="follow-up-begin-call-btn primary" slot="create-button"
        :loading="loading" :disabled="phoneActive || !callId" @click="handleFollowupCall(selectedContact)">
        Call
      </v-btn>
    </application-modal>
  </v-card>
</template>

<script>
/* eslint-disable eqeqeq */
import ApplicationModal from "@/components/ApplicationModal";
import FollowUpDetails from "@/views/dashboard/partials/FollowUpDetails";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import { followUpHeaders } from "@/helpers/dashboard/follow-up-card-data-table-headers";
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  mixins: [dateMethodMixins],
  data() {
    return {
      loading: true,
      emailLoading: false,
      noteLoading: false,
      unitFeaturesLoading: false,

      initCallModal: false,
      submitted: false,
      searchOn: false,

      callId: "",
      facilityId: "",
      facilityInfo: "",
      locationCode: "",
      filteredLocationCode: "",
      filteredTodayOnly: false,
      selectedContact: "",
      notes: [],
      note: "",
      unitFeatures: [],
      promotionOffers: "",
      sortDirection: "asc",
      sortValueActiveIndex: "",
      sortValues: [],
      headers: followUpHeaders,

      followups: [],
      followupsUpdate: null,

      tab: 0
    };
  },

  methods: {
    getTodaysFollowups() {
      this.loading = true;
      this.$store
        .dispatch("getAllFollowups")
        .then((resp) => {
          this.followups = resp.data;
          this.loading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    openCallModal(followup) {
      this.initCallModal = true;
      this.facilityId = followup.facility_id;

      let payload = {
        facility_id: this.facilityId,
        facilityId: this.facilityId,
        waiting_list_id: followup.waiting_list_id,
        concession_id: followup.concession_id ? followup.concession_id : null, // Conditional because some follow-ups are not assigned concession ids within sitelink.
        unit_id: followup.unit_id
      };

      this.selectedContact = followup;
      this.getNotes(payload);
      this.getUnitFeatures(payload);
      this.getFacility(payload);
    },

    toggleSearch() {
      if (this.searchOn === true) {
        this.searchOn = false;
      } else {
        this.searchOn = true;
      }
    },

    clearContact() {
      this.callId = "";
      this.facilityId = "";
      this.selectedContact = "";
      this.notes = [];
      this.note = "";
    },

    timestamp(date) {
      return moment(date).format("MM/DD/YYYY, h:mm a");
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    getFacility(payload) {
      this.$store
        .dispatch("getFacility", payload.facilityId)
        .then((resp) => {
          this.facilityInfo = resp.data.facility;
          payload.facilityId = this.facilityInfo.id;
          payload.externalPromotionId = payload.concession_id;
          this.getPromotionOffers(payload);
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.facilityDataLoading = false;
        });
    },

    getUnitFeatures(payload) {
      this.unitFeaturesLoading = true;
      this.$store
        .dispatch("getUnitFeatures", payload)
        .then((resp) => {
          this.unitFeatures = resp.data;
          this.unitFeaturesLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.unitFeaturesLoading = false;
        });
    },

    getNotes(payload) {
      this.noteLoading = true;

      this.$store
        .dispatch("getNotes", payload)
        .then((resp) => {
          this.notes = this.sortNotes(resp.data);
          this.noteLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.noteLoading = false;
        });
    },

    getPromotionOffers(payload) {
      this.$store.dispatch("getPromotionOptions", payload);
    },

    addNote() {
      this.noteLoading = true;

      let payload = {
        facility_id: this.facilityId,
        waiting_list_id: this.selectedContact.waiting_list_id,
        description: this.note
      };

      this.$store
        .dispatch("addNote", payload)
        .then((resp) => {
          this.noteLoading = false;
          this.$store.dispatch("handleSuccess", "Reservation note added!");
          this.note = "";
          this.notes = this.sortNotes(resp.data);
        })
        .catch((err) => {
          this.noteLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    sortNotes(notes) {
      return notes.sort((a, b) => {
        let date1 = new Date(a.created_at).getTime();
        let date2 = new Date(b.created_at).getTime();
        return date2 - date1;
      });
    },

    handleFollowupCall(contact) {
      let payload = {
        ctm_id: parseFloat(this.callId),
        call_type: "followup",
        location_code: contact.location_code,
        tenant_id: contact.tenant_id,
        external_reservation_id: contact.waiting_list_id
      };

      this.loading = true;
      this.$store
        .dispatch("beginCall", payload)
        .then((resp) => {
          let localStoragePayload = {
            locationCode: payload.location_code,
            reservationId: null,
            callId: resp.data.call.id,
            ctm_id: payload.ctm_id,
            call_type: payload.call_type,
            router: {
              name: "tenantDetails",
              facilityId: resp.facilityId
            },
            contact_id: null,
            tenant_id: contact.tenant_id
          };

          this.$store.dispatch("routeCall", localStoragePayload);

          this.loading = false;
          this.submitted = true;
          this.clearContact();
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    }
  },

  computed: {
    ...mapGetters([
      "promotionOptionsLoading"
    ]),
    facilityCodes() {
      const allFollowupsCodes = this.allFollowups.map(
        (followup) => followup.location_code
      );
      return Array.from(new Set(allFollowupsCodes));
    },

    allFollowups() {
      return this.followups ? this.followups.map(contact => {
        return Object.assign({
          location_code: contact.facility.location_code
        }, contact);
      }) : [];
    },

    selectedFollowups() {
      let filtered = this.allFollowups;

      if (this.filteredLocationCode) {
        filtered = filtered.filter(
          (followup) => followup.location_code === this.filteredLocationCode
        );
      }

      if (this.filteredTodayOnly) {
        const localDate = moment().format("YYYY-MM-DD");
        filtered = filtered.filter((followup) => {
          return followup.followup_at && followup.followup_at.includes(localDate);
        });
      }

      return filtered;
    },

    phoneActive() {
      return localStorage.getItem("callStarted");
    },

    validEmail() {
      let regex = /^\S+@\S+\.\S+$/;
      return regex.test(this.customerEmail);
    }
  },

  created() {
    if (sessionStorage.getItem("access_token")) {
      this.getTodaysFollowups();
      this.followupsUpdate = setInterval(() => {
        this.getTodaysFollowups();
      }, 300000); // check for new followups every 5 min
    }
  },

  beforeDestroy() {
    clearInterval(this.followupsUpdate);
  },

  components: {
    FollowUpDetails,
    ApplicationModal
  }
};
</script>

<style lang="scss">
.no-wrap {
  white-space: nowrap;
}
</style>
